import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import scrollIntoView from 'scroll-into-view';
import isNumber from 'lodash/isNumber';
import { v4 as uuid } from 'uuid';
import { Button, ButtonWithIcon } from '@els/els-react--button';
import { MetadataPropTypesShape } from '@Components/Results/MetadataPropTypes';
import SimpleSpinner from '@Components/Results/utils/SimpleSpinner';

const DEFAULTS = {
  ITEM_THRESHOLD: 100,
  OFFSET_TOP: 200,
  OFFSET_BOTTOM: 50,
  THROTTLE: 100,
};

function ShowMore({
  threshold = DEFAULTS.ITEM_THRESHOLD,
  idSuffix = uuid(),
  scrollContainerSelector,
  container,
  scrollToLess,
  current,
  initial,
  total,
  labelNext,
  labelAll,
  moreHandler,
  lessHandler,
  metadata,
  documentResults,
  isLoading,
  hideIcon = false,
  preventFocusScroll = false,
}) {
  // When hideIcon is set, render plain Leyden buttons with no sprite (RX-23241
  // substance tables show iconless Show next / Show Less buttons).
  const ToggleButton = hideIcon ? Button : ButtonWithIcon;
  const showLessIconProps = hideIcon ? {} : { sprite: 'ChevronUp', iconRight: true };
  const showMoreIconProps = hideIcon ? {} : { sprite: 'Plus' };
  const [isLess, setIsLess] = useState(false);
  const [isMore, setIsMore] = useState(false);
  const [label, setLabel] = useState('');
  const showMoreContainerRef = useRef(null);
  const scrollbarRef = useRef(null);
  // Refs directly on the ButtonWithIcon components (assumes ButtonWithIcon forwards refs)
  const showLessButtonRef = useRef(null);
  const showMoreButtonRef = useRef(null);
  const pendingFocusRef = useRef(false);

  const updateState = () => {
    const remaining = total - current;
    if (remaining >= threshold && !documentResults) {
      setLabel(labelNext || `Show next ${threshold}`);
      setIsMore(true);
    } else if (remaining > 0) {
      setLabel(labelAll || 'Show all');
      setIsMore(true);
    } else {
      setIsMore(false);
    }
  };

  const handleShowMore = () => {
    const firstItem = current + 1;
    const lastItem = total > threshold ? current + threshold : total;

    pendingFocusRef.current = true;

    // Call moreHandler and tolerate both sync and async return values
    const result = moreHandler ? moreHandler({ firstItem, lastItem }) : Promise.resolve();
    Promise.resolve(result)
      .then(() => {
        if (!isLess) {
          setIsLess(true);
          // focus is handled in the dedicated effect below after render
        }
      })
      .catch(() => {
        pendingFocusRef.current = false;
      });
  };

  const handleShowLess = () => {
    const isHeatmapMode = metadata?.heatmapMode;
    const topOffset = isHeatmapMode ? 30 : 140;
    const top = isHeatmapMode ? 0 : null;

    pendingFocusRef.current = true;

    if (lessHandler) lessHandler();
    setIsLess(false);

    scrollIntoView(showMoreContainerRef.current?.closest(scrollToLess || container), {
      behavior: 'smooth',
      align: {
        lockX: true,
        ...(isNumber(top) && { top }),
        ...(isNumber(topOffset) && { topOffset }),
      },
      time: 300,
    });

    updateState();
  };

  useEffect(() => {
    // directly assign to the ref to avoid a redundant local variable
    scrollbarRef.current = scrollContainerSelector ? document.querySelector(scrollContainerSelector) : document;

    updateState();

    if (current > initial && total !== initial) {
      setIsLess(true);
    }
    // We intentionally avoid adding updateState to deps to prevent unnecessary re-creations
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current, initial, scrollContainerSelector, total]);

  // Centralized focus control: when visibility of the buttons changes, focus the newly visible control
  useEffect(() => {
    if (!pendingFocusRef.current || isLoading) return;

    const focusOptions = preventFocusScroll ? { preventScroll: true } : undefined;

    // Focus the ButtonWithIcon DOM node via ref (if forwarded) otherwise this will be a no-op
    if (isLess) {
      const btn = showLessButtonRef.current;
      if (btn && typeof btn.focus === 'function') btn.focus(focusOptions);
      pendingFocusRef.current = false;
    } else if (isMore) {
      const btn = showMoreButtonRef.current;
      if (btn && typeof btn.focus === 'function') btn.focus(focusOptions);
      pendingFocusRef.current = false;
    }
  }, [isLess, isMore, isLoading, preventFocusScroll]);

  if (isLoading) {
    return <SimpleSpinner data-testid="spinner" className="citations-results-spinner" id={`spinner-${idSuffix}`} />;
  }

  return (
    <div
      id={`show-more-${idSuffix}`}
      className={classnames({
        'u-els-display-inline-block': isLess || isMore,
      })}
      ref={showMoreContainerRef}>
      {isLess && (
        <ToggleButton
          ref={showLessButtonRef}
          data-e2e="show-less-button"
          {...showLessIconProps}
          className="show-less-item e2e-show-less u-els-margin-1o2 u-els-margin-left-3o4 u-els-margin-right-3o4"
          type="tertiary"
          size="small"
          onClick={handleShowLess}>
          Show Less
        </ToggleButton>
      )}
      {isMore && (
        <ToggleButton
          ref={showMoreButtonRef}
          data-e2e="show-more-button"
          {...showMoreIconProps}
          className="load-more-item e2e-show-more u-els-margin-1o2 u-els-margin-left-3o4 u-els-margin-right-3o4"
          type="tertiary"
          size="small"
          onClick={handleShowMore}>
          {label}
        </ToggleButton>
      )}
    </div>
  );
}

ShowMore.propTypes = {
  idSuffix: PropTypes.string,
  scrollContainerSelector: PropTypes.string,
  metadata: MetadataPropTypesShape,
  threshold: PropTypes.number,
  container: PropTypes.string.isRequired,
  scrollToLess: PropTypes.string,
  current: PropTypes.number.isRequired,
  initial: PropTypes.number.isRequired,
  total: PropTypes.number,
  labelNext: PropTypes.string,
  labelAll: PropTypes.string,
  moreHandler: PropTypes.func,
  lessHandler: PropTypes.func,
  documentResults: PropTypes.bool,
  isLoading: PropTypes.bool,
  hideIcon: PropTypes.bool,
  preventFocusScroll: PropTypes.bool,
};

export default ShowMore;
